import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

interface StatusWorkflow {
  id: string;
  transition_label: string;
  tooltip: string | null;
  confirmation_msg: string | null;
  comments: 'no' | 'optional' | 'required';
  to_status: {
    id: string;
    name: string;
  };
}

interface StatusTransitionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflow: StatusWorkflow | null;
  onConfirm: (comments?: string) => void;
}

export function StatusTransitionDialog({
  open,
  onOpenChange,
  workflow,
  onConfirm,
}: StatusTransitionDialogProps) {
  const [comments, setComments] = useState('');

  // Reset comments when dialog opens/closes or workflow changes
  useEffect(() => {
    if (!open) {
      setComments('');
    }
  }, [open]);

  if (!workflow) return null;

  const hasConfirmationMsg = !!workflow.confirmation_msg;
  const needsComments = workflow.comments !== 'no';
  const isRequired = workflow.comments === 'required';
  
  const canSubmit = !isRequired || comments.trim().length > 0;

  const handleConfirm = () => {
    onConfirm(needsComments ? comments.trim() || undefined : undefined);
    setComments('');
  };

  const handleCancel = () => {
    onOpenChange(false);
    setComments('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Confirm Status Change</DialogTitle>
          {hasConfirmationMsg && (
            <DialogDescription className="pt-2">
              {workflow.confirmation_msg}
            </DialogDescription>
          )}
        </DialogHeader>

        {needsComments && (
          <div className="space-y-2 py-2">
            <Label htmlFor="transition-comments">
              Comments {isRequired ? '(required)' : '(optional)'}
            </Label>
            <Textarea
              id="transition-comments"
              placeholder="Enter your comments..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
